import { withCloudinaryLogoOptimization } from "../../../utils/cloudinary";

const escapeHtml = (value) => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

const formatDateForPrint = (value) => {
  if (!value) return new Date().toLocaleDateString("en-GB");
  const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? new Date().toLocaleDateString("en-GB") : date.toLocaleDateString("en-GB");
};

const buildAddress = (...parts) => parts
  .map(part => String(part || "").trim())
  .filter(Boolean)
  .join(", ");

export const buildTransportSlipHtml = ({ quotation, customer = {}, branding = {}, assetBaseUrl, inlineCss = "" }) => {
  const senderName = branding.brandName || "Nilam Synthetics";
  const senderAddress = branding.address || "";
  // A quotation is treated as a bill when it carries a bill number. For bills,
  // show both parties' GST numbers right below their names on the slip.
  const isBill = Boolean(String(quotation.bill_number || "").trim());
  const senderGst = branding.ownerGstNumber || "";
  const receiverGst = quotation.customer_gst || customer.gst_number || "";
  const receiverAddress = buildAddress(
    quotation.customer_address || customer.address,
    quotation.customer_city || customer.city,
    quotation.customer_state || customer.state,
    quotation.customer_pincode || customer.pincode
  );
  // Inline the stylesheet whenever possible. An external <link> can still be
  // loading when print() fires, which makes the print engine snapshot an
  // unstyled document (garbled text spread over many pages) even though the
  // on-screen preview eventually looks correct.
  const headStyles = inlineCss
    ? `<style>\n${inlineCss}\n</style>`
    : `<link rel="stylesheet" href="${escapeHtml(`${assetBaseUrl}/css/transportPrint.css`)}" />`;

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Transport Slip ${escapeHtml(quotation.quotation_number || "")}</title>
    ${headStyles}
  </head>
  <body>
    <main class="transport-slip">
      <header class="slip-header">
        ${branding.logoUrl ? `<img class="slip-logo" src="${escapeHtml(withCloudinaryLogoOptimization(branding.logoUrl))}" alt="${escapeHtml(senderName)} logo" />` : ""}
        <div>
          <p class="slip-kicker">TRANSPORT DELIVERY SLIP</p>
          <p class="section-label">FROM: </p>
          <h1>${escapeHtml(senderName)}</h1>
          ${isBill && senderGst ? `<strong><p class="party-gst">GSTIN: ${escapeHtml(senderGst)}</p></strong>` : ""}
          <strong>${senderAddress ? `<p>${escapeHtml(senderAddress)}</p>` : ""}
          ${branding.phone || branding.whatsapp ? `<p>Contact: ${escapeHtml(branding.phone || branding.whatsapp)}</p>` : ""}</strong>
        </div>
        <div class="slip-date"><span>Date</span><strong>${escapeHtml(formatDateForPrint(quotation.created_at))}</strong></div>
      </header>

      <section class="parcel-count"><span>QTY</span><strong>&nbsp;</strong><span>PARCELS</span></section>

      <section class="address-card">
        <p class="section-label">TO: </p>
        <h2>${escapeHtml(quotation.customer_name || customer.customer_name || "—")}</h2>
        ${isBill && receiverGst ? `<strong><p class="party-gst">GSTIN: ${escapeHtml(receiverGst)}</p></strong>` : ""}
        <p class="contact"><strong>Contact: ${escapeHtml(quotation.customer_mobile || customer.mobile_number || "—")}</strong></p>
        <p class="address"><strong>${escapeHtml(receiverAddress || "—")}</p></strong>
      </section>

      <section class="transport-card">
        <p class="section-label">TRANSPORT</p>
        <h2>${escapeHtml(quotation.transport_name || customer.transport_name || "—")}</h2>
      </section>

      <footer>
        Quotation No.: ${escapeHtml(quotation.quotation_number || "—")}<br>
        ${isBill ? `Bill No.: ${escapeHtml(quotation.bill_number || "—")}</p>` : ""}
      </footer>
    </main>
  </body>
</html>`;
};

export const printTransportSlip = async ({ quotation, customers, branding, onError }) => {
  const customer = customers.find(item => item.id === quotation.customer_id) || {};
  const assetBaseUrl = window.location.origin;
  const printWindow = window.open("", "_blank", "width=850,height=1100");
  if (!printWindow) {
    onError?.("Please allow popups to print the transport slip");
    return;
  }

  // Prefer inlining the CSS so the print engine has every style available the
  // instant it snapshots the document (an external stylesheet can still be
  // loading when print() fires, which causes garbled/multi-page output). We
  // fall back to the external <link> if the fetch fails.
  let inlineCss = "";
  try {
    const response = await fetch(`${assetBaseUrl}/css/transportPrint.css`);
    if (response.ok) {
      inlineCss = await response.text();
    }
  } catch (error) {
    inlineCss = "";
  }

  try {
    printWindow.document.open();
    printWindow.document.write(buildTransportSlipHtml({
      quotation,
      customer,
      branding,
      assetBaseUrl,
      inlineCss,
    }));
    printWindow.document.close();
    printWindow.focus();

    const closePrintWindow = () => { if (!printWindow.closed) printWindow.close(); };
    printWindow.onafterprint = closePrintWindow;

    let printed = false;
    const triggerPrint = () => {
      if (printed) return;
      printed = true;
      printWindow.print();
      // Fallback for browsers where `onafterprint` is unreliable.
      window.setTimeout(closePrintWindow, 1500);
    };

    // Wait for the popup to fully render (CSS/fonts/images) before calling
    // print(). Firing print() on a fixed timer can snapshot the document
    // mid-load, printing garbled text spread over many pages even though the
    // on-screen preview eventually looks correct.
    if (printWindow.document.readyState === "complete") {
      window.setTimeout(triggerPrint, 50);
    } else {
      printWindow.addEventListener("load", triggerPrint, { once: true });
      // Safety net in case the load event is missed or blocked.
      window.setTimeout(triggerPrint, 2500);
    }
  } catch (error) {
    printWindow.close();
    onError?.("Unable to open printable transport slip. Please try again.");
  }
};
